import type { ExportFormat } from '@tempo-io/roadmap-dto';

export const visualizationQueryKeys = {
  items: {
    partial: () => ['loop-visualization', 'items'] as const,
    list: ({ filterKey }: { filterKey?: string } = {}) =>
      ['loop-visualization', 'items', { filterKey }] as const,
  },
  loopItem: {
    detail: (itemId: number) => ['loop', 'item', itemId] as const,
  },
  itemsViewLayout: {
    detail: (loopId: number) => ['loop', loopId, 'items', 'layout'] as const,
  },
  viewItems: {
    partial: (viewId: number) => ['view', viewId] as const,
    partialViewItems: (viewId: number) => ['view', viewId, 'items'] as const,
    treeSet: (viewId: number, filterKey?: string) =>
      ['view', viewId, 'items', 'tree-set', { filterKey }] as const,
  },
  snapshot: {
    all: (loopId: number, viewId: number) => ['loop', loopId, 'view', viewId, 'snapshot'] as const,
    items: (loopId: number, viewId: number) =>
      ['loop', loopId, 'view', viewId, 'snapshot', 'items'] as const,
    schedules: (loopId: number, viewId: number) =>
      ['loop', loopId, 'view', viewId, 'snapshot', 'schedules'] as const,
  },
  visualization: {
    all: (viewId: number) => ['visualization', 'list', viewId] as const,
    detail: (viewId: number, visualizationId: number) =>
      ['visualization', 'detail', viewId, visualizationId] as const,
    byType: (viewId: number, type: string) => ['visualization', 'byType', viewId, type] as const,
    loopList: (loopId: number) => ['visualization', 'loop-list', loopId] as const,
    orgList: () => ['visualization', 'org-list'] as const,
  },
  dashboard: {
    all: () => ['dashboard'] as const,
    orgList: () => ['dashboard', 'org-list'] as const,
    detail: (dashboardId: number) => ['dashboard', 'detail', dashboardId] as const,
    previewData: (dashboardId: number) =>
      ['dashboard', 'detail', dashboardId, 'preview-data'] as const,
    filter: {
      list: (dashboardId: number) => ['dashboard', dashboardId, 'filter', 'list'] as const,
      detail: (dashboardId: number, fieldId: string) =>
        ['dashboard', dashboardId, 'filter', 'detail', fieldId] as const,
    },
  },
  widget: {
    detail: (widgetId: number) => ['widget', 'detail', widgetId] as const,
  },
  swimlaneLayout: {
    detail: (viewId: number, visualizationId: number) =>
      ['swimlane-layout', viewId, visualizationId] as const,
    headerConfig: (
      viewId: number,
      visualizationId: number,
      headerFieldId: string,
      parentFieldId?: string | null,
      parentHeaderId?: string | null,
    ) =>
      [
        'swimlane-layout',
        'header-config',
        viewId,
        visualizationId,
        headerFieldId,
        parentFieldId ?? null,
        parentHeaderId ?? null,
      ] as const,
  },
  nodeGraphLayout: {
    detail: (viewId: number, visualizationId: number) =>
      ['node-graph-layout', viewId, visualizationId] as const,
  },
  tableLayout: {
    all: () => ['table-layout'] as const,
    detail: (viewId: number, visualizationId: number) =>
      [...visualizationQueryKeys.tableLayout.all(), viewId, visualizationId] as const,
  },
  itemRanks: {
    detail: (viewId: number, visualizationId: number) =>
      ['item-ranks', viewId, visualizationId] as const,
  },
  timelineLayout: {
    detail: (viewId: number, visualizationId: number) =>
      ['timeline-layout', viewId, visualizationId] as const,
    headerConfig: ({
      viewId,
      visualizationId,
      headerFieldId,
      parentFieldId,
      parentHeaderId,
    }: {
      viewId: number;
      visualizationId: number;
      headerFieldId: string;
      parentFieldId?: string | null;
      parentHeaderId?: string | null;
    }) =>
      [
        'timeline-layout',
        'header-config',
        viewId,
        visualizationId,
        headerFieldId,
        parentFieldId,
        parentHeaderId,
      ] as const,
  },
  publishedVisualization: {
    detail: (viewId: number, visualizationId: number) =>
      ['published-visualization', viewId, visualizationId] as const,
  },
  exportStatus: {
    detail: (viewId: number, visualizationId: number, format: ExportFormat) =>
      ['export-status', viewId, visualizationId, format] as const,
    dashboard: (dashboardId: number, format: ExportFormat) =>
      ['export-status', 'dashboard', dashboardId, format] as const,
  },
  nodeGraphState: {
    detail: (visualizationId: number) => ['node-graph-state', visualizationId] as const,
  },
};
